/**
 * Voice definitions — the single source of truth for voice identity.
 *
 * `VOICE_DEFS` below is the ONE place voices are declared. Everything else in
 * the repo derives from it:
 *   - `VoiceModelId` / `VoiceDisplayName` (schemas + types) are built from the
 *     `model_id` / `name` fields of `VOICE_DEFS` — no parallel literal lists.
 *   - `VOICE_RESOLUTION_MAP` (display-name/model_id → model_id) is derived.
 *   - `@listening/backend/kokoro` re-exports `VOICE_DEFS` and the resolution
 *     helpers, and `apps/api` maps `VOICE_DEFS` into the `/voices` response.
 *
 * To add a voice: add one entry to `VOICE_DEFS`. `lang` is constrained to the
 * canonical `SupportedLanguage` set (see ./audio-dtos), so a voice in an
 * unsupported language is a compile error.
 */
import { Effect, Schema, SchemaGetter, SchemaIssue } from 'effect';
import {
  AudioConversionLanguageSchema,
  type SupportedLanguage,
} from './audio-dtos';

// ---------------------------------------------------------------------------
// VOICE_DEFS — canonical voice list
// ---------------------------------------------------------------------------

export interface VoiceDef {
  readonly name: string;
  readonly lang: SupportedLanguage;
  readonly model_id: string;
  readonly softness?: 'soft' | 'moderate' | 'deep';
  readonly is_free?: boolean;
  /**
   * Retired from the UI: still resolvable (so existing API requests and stored
   * user preferences keep working) but never offered as a choice. Use
   * `VISIBLE_VOICE_DEFS` for anything a user picks from.
   */
  readonly hidden?: boolean;
}

export const VOICE_DEFS = [
  // English — Legacy
  { name: 'Monica', lang: 'en', model_id: 'af_sky' },
  { name: 'Rupert', lang: 'en', model_id: 'am_michael' },
  { name: 'Liv', lang: 'en', model_id: 'af_bella' },
  { name: 'Scarlett', lang: 'en', model_id: 'af_heart', is_free: true },
  { name: 'Will', lang: 'en', model_id: 'am_liam', is_free: true },
  { name: 'Amy', lang: 'en', model_id: 'bf_emma', softness: 'soft' },
  { name: 'Leslie', lang: 'en', model_id: 'af_aoede' },
  { name: 'Beatrix', lang: 'en', model_id: 'af_kore' },
  { name: 'Allison', lang: 'en', model_id: 'af_sarah' },
  { name: 'Valerie', lang: 'en', model_id: 'af_alloy' },
  { name: 'Elizabeth', lang: 'en', model_id: 'af_nova' },
  { name: 'Tyler', lang: 'en', model_id: 'am_puck' },
  { name: 'Zachary', lang: 'en', model_id: 'bm_fable' },
  { name: 'Justin', lang: 'en', model_id: 'bm_george' },
  { name: 'Hugh', lang: 'en', model_id: 'bm_lewis', softness: 'deep' },
  { name: 'Dan', lang: 'en', model_id: 'am_echo' },
  { name: 'Millie', lang: 'en', model_id: 'bf_v0isabella', softness: 'soft' },
  { name: 'Harvey', lang: 'en', model_id: 'am_eric' },
  { name: 'Aaron', lang: 'en', model_id: 'am_fenrir' },
  { name: 'Claire', lang: 'en', model_id: 'af_nicole' },
  // English — New
  { name: 'Lewis', lang: 'en', model_id: 'bm_v0lewis' },
  { name: 'Jessica', lang: 'en', model_id: 'af_jessica' },
  { name: 'Sky', lang: 'en', model_id: 'af_v0sky' },
  { name: 'Santa', lang: 'en', model_id: 'am_santa' },
  { name: 'Daniel', lang: 'en', model_id: 'bm_daniel' },
  { name: 'River', lang: 'en', model_id: 'af_river' },
  { name: 'Michael', lang: 'en', model_id: 'am_v0michael' },
  { name: 'Alice', lang: 'en', model_id: 'bf_alice' },
  { name: 'Aria', lang: 'en', model_id: 'af_v0' },
  { name: 'Adam', lang: 'en', model_id: 'am_adam' },
  { name: 'Lily', lang: 'en', model_id: 'bf_lily' },
  { name: 'Irulan', lang: 'en', model_id: 'af_v0irulan', hidden: true },
  { name: 'Emma', lang: 'en', model_id: 'bf_v0emma' },
  { name: 'Bella', lang: 'en', model_id: 'af_v0bella' },
  { name: 'Sarah', lang: 'en', model_id: 'af_v0sarah' },
  { name: 'Nicole', lang: 'en', model_id: 'af_v0nicole' },
  { name: 'Onyx', lang: 'en', model_id: 'am_onyx' },
  { name: 'Marcus', lang: 'en', model_id: 'am_v0adam' },
  { name: 'Gurney', lang: 'en', model_id: 'am_v0gurney' },
  { name: 'Jadzia', lang: 'en', model_id: 'af_jadzia' },
  { name: 'George', lang: 'en', model_id: 'bm_v0george' },
  // Spanish
  { name: 'Carmen', lang: 'es', model_id: 'ef_dora', is_free: true },
  { name: 'Diego', lang: 'es', model_id: 'em_alex', is_free: true },
  { name: 'Pablo', lang: 'es', model_id: 'em_santa' },
  // French
  { name: 'Siwis', lang: 'fr', model_id: 'ff_siwis', is_free: true },
  // Hindi
  { name: 'Priya', lang: 'hi', model_id: 'hf_alpha', is_free: true },
  { name: 'Beta', lang: 'hi', model_id: 'hf_beta' },
  { name: 'Omega', lang: 'hi', model_id: 'hm_omega', is_free: true },
  { name: 'Psi', lang: 'hi', model_id: 'hm_psi' },
  // Italian
  { name: 'Sara', lang: 'it', model_id: 'if_sara', is_free: true },
  { name: 'Nicola', lang: 'it', model_id: 'im_nicola', is_free: true },
  // Japanese
  { name: 'Yuki', lang: 'ja', model_id: 'jf_alpha', is_free: true },
  { name: 'Gongitsune', lang: 'ja', model_id: 'jf_gongitsune' },
  { name: 'Nezumi', lang: 'ja', model_id: 'jf_nezumi' },
  { name: 'Tebukuro', lang: 'ja', model_id: 'jf_tebukuro' },
  { name: 'Kumo', lang: 'ja', model_id: 'jm_kumo', is_free: true },
  // Portuguese
  { name: 'Isabel', lang: 'pt', model_id: 'pf_dora', is_free: true },
  { name: 'Carlos', lang: 'pt', model_id: 'pm_alex', is_free: true },
  { name: 'Natalia', lang: 'pt', model_id: 'pm_santa' },
  // Chinese
  { name: 'Xiaobei', lang: 'zh', model_id: 'zf_xiaobei', is_free: true },
  { name: 'Xiaoni', lang: 'zh', model_id: 'zf_xiaoni' },
  { name: 'Xiaoxiao', lang: 'zh', model_id: 'zf_xiaoxiao' },
  { name: 'Xiaoyi', lang: 'zh', model_id: 'zf_xiaoyi' },
  { name: 'Yunjian', lang: 'zh', model_id: 'zm_yunjian', is_free: true },
  { name: 'Yunxi', lang: 'zh', model_id: 'zm_yunxi' },
  { name: 'Yunxia', lang: 'zh', model_id: 'zm_yunxia' },
  { name: 'Yunyang', lang: 'zh', model_id: 'zm_yunyang' },
] as const satisfies readonly VoiceDef[];

/**
 * The voices users may pick from — `VOICE_DEFS` minus the `hidden` ones.
 *
 * Every user-facing list (the `/voices` response, the frontend predefined
 * catalog, marketing demos) must derive from THIS, not `VOICE_DEFS`. Resolution
 * (`VoiceInput`, `resolveToModelId`, …) intentionally still spans the full
 * `VOICE_DEFS` so a request for a hidden voice keeps succeeding.
 */
export const VISIBLE_VOICE_DEFS = VOICE_DEFS.filter(
  (v) => !('hidden' in v && v.hidden),
) as readonly Exclude<(typeof VOICE_DEFS)[number], { hidden: true }>[];

const HIDDEN_VOICE_KEYS: ReadonlySet<string> = new Set(
  VOICE_DEFS.filter((v) => 'hidden' in v && v.hidden).flatMap((v) => [
    v.name.toLowerCase(),
    v.model_id.toLowerCase(),
  ]),
);

/**
 * True for a voice that must not be offered in any picker, by display name or
 * model_id (any case). Clients apply this to catalogs that can outlive a
 * release — the persisted voice cache, an older server's `/voices` response.
 */
export function isHiddenVoice(nameOrModelId: string): boolean {
  return HIDDEN_VOICE_KEYS.has(nameOrModelId.toLowerCase());
}

/** Default voice used when no voice preference is set. */
export const DEFAULT_VOICE = 'Will';

// ---------------------------------------------------------------------------
// VoiceModelId / VoiceDisplayName — branded strings
// ---------------------------------------------------------------------------

// Open branded strings, not closed literal unions (cf.
// docs/cloned-repos-as-docs/effect-typed-id and opencode's branded IDs). The
// brand makes the two voice vocabularies nominally disjoint — a display name
// can't be passed where a model_id is expected without an `as unknown as`
// double-cast (which `anti-slop/no-chained-type-assertions` flags) — while the open value
// set keeps wire decoding version-skew-safe: adding a voice to VOICE_DEFS must
// not break stale clients decoding `/voices` or TTS responses. Closed-set
// validation lives where the set is enforceable: `VoiceInput` decode at the
// server boundary, and the `knownVoice*` helpers for in-repo literals.
export const VoiceModelId = Schema.String.pipe(Schema.brand('VoiceModelId'));
export type VoiceModelId = typeof VoiceModelId.Type;

export const VoiceDisplayName = Schema.String.pipe(
  Schema.brand('VoiceDisplayName'),
);
export type VoiceDisplayName = typeof VoiceDisplayName.Type;

// Compile-time literal sets derived from VOICE_DEFS (`as const` preserves the
// literal types). Use these to typo-check literals; use the branded types
// above for runtime values.
export type KnownVoiceModelId = (typeof VOICE_DEFS)[number]['model_id'];
export type KnownVoiceDisplayName = (typeof VOICE_DEFS)[number]['name'];

/** A voice users may be shown — hiding a voice drops its name from this type, so surfaces that hardcode a name fail typecheck instead of throwing at render. */
export type VisibleVoiceDisplayName =
  (typeof VISIBLE_VOICE_DEFS)[number]['name'];

/** Brand a model_id that provably exists in VOICE_DEFS — a typo'd literal is a compile error. */
export function knownVoiceModelId(id: KnownVoiceModelId): VoiceModelId {
  return VoiceModelId.make(id);
}

/** Brand a display name that provably exists in VOICE_DEFS — a typo'd literal is a compile error. */
export function knownVoiceDisplayName(
  name: KnownVoiceDisplayName,
): VoiceDisplayName {
  return VoiceDisplayName.make(name);
}

// ---------------------------------------------------------------------------
// Derived lookups + resolution
// ---------------------------------------------------------------------------

const NAME_TO_MODEL_ID: ReadonlyMap<string, VoiceModelId> = new Map(
  VOICE_DEFS.map((v) => [v.name, VoiceModelId.make(v.model_id)]),
);

// Case-insensitive lookup: lowercase name/model_id → canonical voice name
const LOWERCASE_TO_CANONICAL_NAME: ReadonlyMap<string, string> = new Map(
  VOICE_DEFS.flatMap((v) => [
    [v.name.toLowerCase(), v.name],
    [v.model_id.toLowerCase(), v.name],
  ]),
);

// Case-insensitive lookup: lowercase name/model_id → model_id
const VOICE_RESOLUTION_MAP: ReadonlyMap<string, VoiceModelId> = new Map(
  VOICE_DEFS.flatMap((v) => [
    [v.name.toLowerCase(), VoiceModelId.make(v.model_id)],
    [v.model_id.toLowerCase(), VoiceModelId.make(v.model_id)],
  ]),
);

/**
 * Normalize a voice identifier to its canonical display name.
 *
 * Case-insensitive — accepts "WILL", "will", "Will", or model_id "am_liam".
 * Returns the canonical name ("Will") or the input unchanged if unrecognized.
 * Matches legacy Python API behavior (get_supported_voice_id).
 */
export function normalizeVoiceName(voice: string): string {
  return LOWERCASE_TO_CANONICAL_NAME.get(voice.toLowerCase()) ?? voice;
}

/**
 * Resolve a voice name or model_id to a Kokoro model_id.
 *
 * Accepts both display names ("Will") and raw model_ids ("am_liam").
 * Returns the input unchanged if it's already a valid model_id.
 */
export function resolveKokoroModelId(voice: string) {
  return NAME_TO_MODEL_ID.get(voice) ?? voice;
}

/**
 * Resolve any voice identifier (display name or model_id, any case) to a VoiceModelId.
 *
 * Throws if the voice is not recognized. For non-throwing resolution at the HTTP
 * boundary, use the `VoiceInput` schema below.
 */
export function resolveToModelId(voice: string): VoiceModelId {
  const resolved = VOICE_RESOLUTION_MAP.get(voice.toLowerCase());
  if (!resolved) throw new Error(`Unknown voice: ${voice}`);
  return resolved;
}

/**
 * Resolves a voice input string (display name or model_id, any case) to a VoiceModelId.
 * Exported for use in runtime resolution (e.g. DB fallback values, test seeding).
 */
export function resolveVoiceInput(input: string): VoiceModelId | undefined {
  return VOICE_RESOLUTION_MAP.get(input.toLowerCase());
}

// ---------------------------------------------------------------------------
// VoiceInput — SchemaGetter.transformEffect: any-case string → VoiceModelId
// ---------------------------------------------------------------------------

/**
 * Schema that accepts any-case voice display name or model_id and transforms to VoiceModelId.
 *
 * Use this as the type for voice input parameters in API endpoints.
 * Invalid/unknown voices produce a ParseError (400 Bad Request).
 */
export const VoiceInput = Schema.String.pipe(
  Schema.decodeTo(VoiceModelId, {
    decode: SchemaGetter.transformEffect((input) => {
      const resolved = VOICE_RESOLUTION_MAP.get(input.toLowerCase());
      return resolved
        ? Effect.succeed(resolved)
        : Effect.fail(
            new SchemaIssue.InvalidValue(
              { message: `Unknown voice: ${input}` },
              input,
            ),
          );
    }),
    encode: SchemaGetter.passthrough(),
  }),
);

// ---------------------------------------------------------------------------
// VoiceSummary / GetListVoices200 — the canonical `/voices` response shape
// ---------------------------------------------------------------------------

/**
 * Wire schema for a single voice in the `GET /v2/voices` response.
 *
 * Defined HERE from the `VOICE_DEFS`-derived unions (`VoiceModelId`,
 * `VoiceDisplayName`) and the canonical language set — NOT regenerated from a
 * deployed server's OpenAPI. That is the whole point: renaming or removing a
 * voice in `VOICE_DEFS` is a compile error everywhere this type flows (the
 * endpoint impl, the `defaultMaleVoice`/`defaultFemaleVoice` constants, every
 * client consumer) instead of a silent runtime drift. The field shape is kept
 * identical to the historical generated schema for wire compatibility:
 * `is_free`/`softness` stay `optionalWith({ nullable, default })`, `name`
 * tightens from `string` to `VoiceDisplayName`. `gender`, `image_url` and
 * `sample_url` are computed at the endpoint (gender from the model_id, the URLs
 * from config), so they are plain fields here.
 *
 * A plain `Schema.Struct` (not `Schema.Class`): `Voice` is a DTO that the
 * frontend already flattens via `schemaInstanceToPlain` and spreads into props,
 * so decoding to plain objects is correct and avoids class-instance spread
 * hazards. The `identifier` annotation keeps the OpenAPI component named
 * `VoiceSummary`.
 */
export const VoiceSummary = Schema.Struct({
  gender: Schema.Literals(['male', 'female']),
  image_url: Schema.String,
  is_free: Schema.NullOr(Schema.Boolean).pipe(
    Schema.decodeTo(Schema.toType(Schema.Boolean), {
      decode: SchemaGetter.transform((value) => value ?? false),
      encode: SchemaGetter.passthroughSubtype<boolean | null, boolean>(),
    }),
    Schema.withDecodingDefaultType(Effect.succeed(false)),
  ),
  lang: AudioConversionLanguageSchema,
  model_id: VoiceModelId,
  name: VoiceDisplayName,
  sample_url: Schema.String,
  softness: Schema.NullOr(Schema.Literals(['soft', 'moderate', 'deep'])).pipe(
    Schema.decodeTo(
      Schema.toType(Schema.Literals(['soft', 'moderate', 'deep'])),
      {
        decode: SchemaGetter.transform((value) => value ?? 'moderate'),
        encode: SchemaGetter.passthroughSubtype<
          'soft' | 'moderate' | 'deep' | null,
          'soft' | 'moderate' | 'deep'
        >(),
      },
    ),
    Schema.withDecodingDefaultType(Effect.sync(() => 'moderate' as const)),
  ),
}).annotate({ identifier: 'VoiceSummary' });
export type VoiceSummary = typeof VoiceSummary.Type;

/** Response schema for `GET /v2/voices`. */
export const GetListVoices200 = Schema.Struct({
  voices: Schema.Array(VoiceSummary),
});
